import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MultiSelect } from '../../../../components/other/MultiSelect';
import { getFacilitiesByCompanySettings } from '../../../../services/facility';
import { getCompanyUserGroupListAccessSettings } from '../../../../services/company';
import { getUsersListSettings } from '../../../../services/user';

export const EMPTY_ACCESS_SETTINGS = { facilities: [], user_groups: [], individual_users: [] };

const toOption = (item, label) => ({ value: item.id, label });
const userLabel = (user) => `${user.last_name} ${user.first_name}`;

// The task comes back with the settings as objects (the way the form access
// settings do); the selects work on {value, label} pairs.
export const toAccessSettingsState = (accessSettings) => ({
  facilities: (accessSettings?.facilities || []).map((item) => toOption(item, item.name)),
  user_groups: (accessSettings?.user_groups || []).map((item) => toOption(item, item.name)),
  individual_users: (accessSettings?.individual_users || []).map((item) => toOption(item, userLabel(item))),
});

export const toAccessSettingsBody = (accessSettings) => ({
  facilities: accessSettings.facilities.map((item) => item.value),
  user_groups: accessSettings.user_groups.map((item) => item.value),
  individual_users: accessSettings.individual_users.map((item) => item.value),
});

// A selection survives a reload of its options only if it is still offered:
// a user group of a facility that was just dropped is gone with it.
const keepOffered = (selected, options) => selected.filter((item) => options.some((option) => option.value == item.value));

const byLabel = (a, b) => a.label.localeCompare(b.label);

/**
 * Who may initiate the task -- the "Access Settings" of the task modal and of
 * the task's Access Settings tab.
 *
 * Optional: with no facility chosen the task is left to the super admin and the
 * company system admin. User groups and individual users are offered only once
 * a facility is chosen, and only from within the chosen facilities.
 *
 * The modal shows it as one of its cards, under its own title; the tab has a
 * heading of its own, so it takes the fields alone (``showTitle``).
 */
const TaskAccessSettings = ({ companyId, value, onChange, errors, showTitle = true }) => {
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [userGroupOptions, setUserGroupOptions] = useState([]);
  const [individualUserOptions, setIndividualUserOptions] = useState([]);

  // The option loaders finish after the user may have picked again, so they
  // prune whatever is selected by then, not what was selected when they started.
  const valueRef = useRef(value);
  valueRef.current = value;

  const facilitiesIds = useMemo(() => value.facilities.map((item) => item.value), [value.facilities]);
  const userGroupsIds = useMemo(() => value.user_groups.map((item) => item.value), [value.user_groups]);
  const facilitiesKey = JSON.stringify(facilitiesIds);
  const userGroupsKey = JSON.stringify(userGroupsIds);

  const change = useCallback((patch) => onChange({ ...valueRef.current, ...patch }), [onChange]);

  useEffect(() => {
    getFacilitiesByCompanySettings(companyId, 'getAll')
      .then((facilities) => setFacilityOptions(facilities.map((item) => toOption(item, item.name))))
      .catch((e) => console.log(e));
  }, [companyId]);

  useEffect(() => {
    if (!facilitiesIds.length) {
      setUserGroupOptions([]);
      return;
    }

    getCompanyUserGroupListAccessSettings({ company_id: companyId, status: 'active', facilities_ids: facilitiesIds })
      .then((groups) => {
        const options = groups.map((item) => toOption(item, item.name));
        setUserGroupOptions(options);
        change({ user_groups: keepOffered(valueRef.current.user_groups, options) });
      })
      .catch((e) => console.log(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, facilitiesKey]);

  useEffect(() => {
    if (!facilitiesIds.length) {
      setIndividualUserOptions([]);
      return;
    }

    getUsersListSettings({
      company_id: companyId,
      status: 'active',
      facilities_ids: facilitiesIds,
      user_groups_ids: userGroupsIds,
    })
      .then((users) => {
        const options = users.map((item) => toOption(item, userLabel(item)));
        setIndividualUserOptions(options);
        change({ individual_users: keepOffered(valueRef.current.individual_users, options) });
      })
      .catch((e) => console.log(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, facilitiesKey, userGroupsKey]);

  const handleFacilitiesChange = (facilities) => {
    // Nothing below a facility outlives it; with no facility at all the task
    // goes back to the admins, so the narrower choices go too.
    if (!facilities.length) {
      change({ facilities: [], user_groups: [], individual_users: [] });
    } else {
      change({ facilities });
    }
  };

  const renderSelected = (items, onRemove, className) =>
    items.length > 0 && (
      <ul className={className}>
        {items.map((item) => (
          <li key={item.value}>
            {item.label}
            <button type="button" className="btn btn-icon" onClick={() => onRemove(item)} aria-label={`Unselect ${item.label}`}>
              <img src="/images/x-circle.svg" alt="" />
            </button>
          </li>
        ))}
      </ul>
    );

  return (
    <div className="group task-access-settings-group">
      {showTitle && <span className="group__title">Access Settings</span>}
      <p className="task-access-settings-hint">
        <i className="icon-icon-info"></i> Select users who will be allowed to initiate the Task. If no one is selected, only the Super
        Admin and the Company System Administrator can initiate it.
      </p>

      <div className="group__body">
        <div className={`form-item form-item-select form-control-item-settings ${errors?.facilities ? 'form-item-error' : ''}`}>
          <label className="form-label">Facilities</label>
          <div className="select-multy-wrapper">
            <MultiSelect
              isDisabled={!facilityOptions.length}
              options={facilityOptions}
              value={value.facilities}
              onChange={handleFacilitiesChange}
              placeholder="Choose one or several facilities"
              allName="All facilities"
              otherSelectProps={{ 'aria-label': 'Facilities' }}
            />
            {renderSelected(value.facilities, (item) =>
              handleFacilitiesChange(value.facilities.filter((facility) => facility.value !== item.value)),
            )}
          </div>
          <span className="form-error mt-1 d-flex">{errors?.facilities && errors.facilities[0]}</span>
        </div>

        <div className={`form-item form-item-select form-control-item-settings ${errors?.user_groups ? 'form-item-error' : ''}`}>
          <label className="form-label">User Groups</label>
          <div className="select-multy-wrapper">
            <MultiSelect
              isDisabled={!facilitiesIds.length || !userGroupOptions.length}
              options={userGroupOptions}
              value={value.user_groups}
              onChange={(user_groups) => change({ user_groups })}
              placeholder={facilitiesIds.length ? 'Choose one or several user groups' : 'Select facilities first'}
              allName="All user groups"
              otherSelectProps={{ 'aria-label': 'User Groups' }}
            />
            {renderSelected(value.user_groups, (item) =>
              change({ user_groups: value.user_groups.filter((group) => group.value != item.value) }),
            )}
          </div>
          <span className="form-error mt-1 d-flex">{errors?.user_groups && errors.user_groups[0]}</span>
        </div>

        <div className={`form-item form-item-select form-control-item-settings ${errors?.individual_users ? 'form-item-error' : ''}`}>
          <label className="form-label">Individual Users</label>
          <div className="select-multy-wrapper">
            <MultiSelect
              isDisabled={!facilitiesIds.length || !individualUserOptions.length}
              options={individualUserOptions}
              value={value.individual_users}
              onChange={(individual_users) => change({ individual_users })}
              placeholder={facilitiesIds.length ? 'Choose one or several users' : 'Select facilities first'}
              allName="All individual users"
              otherSelectProps={{ 'aria-label': 'Individual Users' }}
            />
            {renderSelected(
              [...value.individual_users].sort(byLabel),
              (item) => change({ individual_users: value.individual_users.filter((user) => user.value !== item.value) }),
              'individual-users-list',
            )}
          </div>
          <span className="form-error mt-1 d-flex">{errors?.individual_users && errors.individual_users[0]}</span>
        </div>
      </div>
    </div>
  );
};

export default TaskAccessSettings;
